/* eslint-disable max-params */
/* eslint-disable @typescript-eslint/naming-convention */
import { TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  WcgButtonComponent,
  WcgDialogComponent,
  WcgInputFieldComponent,
  WcgUniSelectComponent,
} from '@wcg/frontend-framework';
import { distinctUntilChanged, filter, skip } from 'rxjs';
import { InviteUserDialogData, InviteUserFormControls } from '../../../types';
import { isEqual } from 'lodash';
import {
  organizationRoleHints,
  updateValueValidity,
} from '@data-access/shared';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    WcgButtonComponent,
    WcgDialogComponent,
    TitleCasePipe,
    WcgInputFieldComponent,
    WcgUniSelectComponent,
  ],
  selector: 'wcg-invite-user-dialog',
  styleUrls: ['./invite-user-dialog.component.scss'],
  templateUrl: 'invite-user-dialog.component.html',
})
export class InviteUserDialogComponent {
  private readonly _dialogRef = inject(MatDialogRef<InviteUserDialogComponent>);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _formBuilder = inject(FormBuilder);
  public readonly data = inject<InviteUserDialogData>(MAT_DIALOG_DATA);

  readonly organizationRoleHintMapper = organizationRoleHints;
  inviteUserForm = this._createForm();

  emailExists = signal<boolean>(false);

  constructor() {
    this._customListener();
  }

  close(): void {
    this._dialogRef.close();
  }

  checkIfEmailExists(): void {
    const _email = this.inviteUserForm.get('email')?.value;
    if (!_email?.length || this.inviteUserForm.controls.email.invalid) {
      return;
    }
    this.data.callBacks.checkEmailExists(_email!);
  }

  private _createForm(): FormGroup<InviteUserFormControls> {
    return this._formBuilder.group<InviteUserFormControls>({
      email: this._formBuilder.control<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
      }),
      firstName: this._formBuilder.control<string>('', {
        nonNullable: true,
      }),
      lastName: this._formBuilder.control<string>('', {
        nonNullable: true,
      }),
      role: this._formBuilder.control(null, {
        nonNullable: false,
        validators: [Validators.required],
      }),
    });
  }

  private _customListener(): void {
    toObservable(this.data.$emailExists)
      .pipe(
        skip(1),
        distinctUntilChanged((a, b) => isEqual(a, b)),
        filter((exists) => exists !== undefined),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (exists) => {
          this.emailExists.update(() => !exists);
          if (exists) {
            this.inviteUserForm.controls.firstName.clearValidators();
            this.inviteUserForm.controls.lastName.clearValidators();
          } else {
            this.inviteUserForm.controls.firstName.setValidators(
              Validators.required,
            );
            this.inviteUserForm.controls.lastName.setValidators(
              Validators.required,
            );
          }
          updateValueValidity(this.inviteUserForm);
        },
      });
    toObservable(this.data.$canCloseDialog)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (canClose) => {
          if (canClose) {
            this._dialogRef.close();
          }
        },
      });
  }
}
